import { Field, fieldErrorId } from "@/components/field"
import { Input } from "@/components/ui/input"
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select"
import type { PaymentMatchingRule } from "@/types"

type MatchType = PaymentMatchingRule["match_type"]

// "IBAN" compares the IBAN of the payment's other side, the others the payment's description and
// reference. Only a counterparty rule matches by IBAN.
const MATCH_TYPE_LABELS: Record<MatchType, string> = {
  includes: "Includes",
  starts_with: "Starts with",
  exact: "Exact",
  iban: "IBAN",
}

export function matchTypeLabel(matchType: MatchType) {
  return MATCH_TYPE_LABELS[matchType]
}

/** How a rule recognizes a payment: the match type, and the text or IBAN to look for. */
export function MatchFields({
  idPrefix,
  matchTypes,
  matchType,
  matchTypeErrors,
  matchText,
  matchTextErrors,
  onMatchType,
  onMatchText,
}: {
  idPrefix: string
  matchTypes: MatchType[]
  matchType: MatchType
  matchTypeErrors?: string[]
  matchText: string
  matchTextErrors?: string[]
  onMatchType: (matchType: MatchType) => void
  onMatchText: (matchText: string) => void
}) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <Field id={`${idPrefix}-match-type`} label="Match type" errors={matchTypeErrors}>
        <NativeSelect
          id={`${idPrefix}-match-type`}
          value={matchType}
          onChange={(event) => onMatchType(event.target.value as MatchType)}
          aria-invalid={matchTypeErrors ? true : undefined}
          aria-describedby={matchTypeErrors ? fieldErrorId(`${idPrefix}-match-type`) : undefined}
        >
          {matchTypes.map((type) => (
            <NativeSelectOption key={type} value={type}>
              {matchTypeLabel(type)}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>
      <Field
        id={`${idPrefix}-match-text`}
        // A text match reads the payment's description and its reference.
        label={matchType === "iban" ? "IBAN" : "Payment text"}
        errors={matchTextErrors}
        className="md:col-span-2"
      >
        <Input
          id={`${idPrefix}-match-text`}
          value={matchText}
          onChange={(event) => onMatchText(event.target.value)}
          aria-invalid={matchTextErrors ? true : undefined}
          aria-describedby={matchTextErrors ? fieldErrorId(`${idPrefix}-match-text`) : undefined}
        />
      </Field>
    </div>
  )
}

/** A part of a rule that cannot be changed, shown like a field. */
export function ReadOnlyValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-2">
      <span className="text-sm font-medium">{label}</span>
      <div className="rounded-md border bg-surface-subtle px-3 py-2 text-sm text-foreground">{value}</div>
    </div>
  )
}

/** Errors of a rule that belong to no single field, such as a duplicate. */
export function BaseErrors({ errors }: { errors?: string[] }) {
  if (!errors) return null

  return (
    <p role="alert" className="text-sm text-destructive">
      {errors.join(", ")}
    </p>
  )
}
